import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const ACCESS_KEY = "cc_access_token";
const REFRESH_KEY = "cc_refresh_token";

export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set: (access, refresh) => {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

const api = axios.create({
  baseURL: API,
});

// Inietta il Bearer token (localStorage) in ogni richiesta.
api.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Silent refresh: al 401 rinnova l'access token via /auth/refresh (refresh token da localStorage) e ritenta.
let isRefreshing = false;
let pendingQueue = [];
const flushQueue = (error) => {
  pendingQueue.forEach(({ resolve, reject, config }) => {
    if (error) reject(error);
    else resolve(api(config));
  });
  pendingQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error;
    if (!response || !config) return Promise.reject(error);
    const url = config.url || "";
    const isAuthEndpoint =
      url.includes("/auth/login") ||
      url.includes("/auth/me") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout");
    if (response.status !== 401 || isAuthEndpoint || config._retry) {
      return Promise.reject(error);
    }
    config._retry = true;
    if (isRefreshing) {
      return new Promise((resolve, reject) => pendingQueue.push({ resolve, reject, config }));
    }
    isRefreshing = true;
    try {
      const refresh = tokenStore.getRefresh();
      if (!refresh) throw new Error("No refresh token");
      const { data } = await api.post("/auth/refresh", { refresh_token: refresh });
      tokenStore.set(data.access_token, null);
      flushQueue(null);
      return api(config);
    } catch (e) {
      flushQueue(e);
      tokenStore.clear();
      window.dispatchEvent(new CustomEvent("auth:logout"));
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Formattatore valuta '€ 1.234,56' — forziamo il grouping perché in it-IT
// (CLDR minimumGroupingDigits=2) i numeri 1000-9999 verrebbero senza punto (es. "3400,00").
const _fmtNum = (n, opts) => {
  const val = Number(n || 0);
  const sign = val < 0 ? "-" : "";
  const abs = Math.abs(val);
  const parts = new Intl.NumberFormat("it-IT", opts).formatToParts(abs);
  // Ricostruisci inserendo il separatore migliaia anche per gruppi da 4 cifre.
  const integer = parts.filter(p => p.type === "integer").map(p => p.value).join("");
  const decimal = parts.find(p => p.type === "decimal")?.value || "";
  const fraction = parts.find(p => p.type === "fraction")?.value || "";
  const withGroup = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${withGroup}${decimal}${fraction}`;
};

export const euro = (n) =>
  `€ ${_fmtNum(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const num = (n) =>
  _fmtNum(n, { maximumFractionDigits: 2 });

export const formatDate = (v) => {
  if (!v) return "—";
  const s = String(v).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : s;
};

// Data e ora nel formato GG/MM/AAAA - HH:MM (ora locale).
export const formatDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "—";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} - ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export async function downloadFile(path, filename, params = {}) {
  const res = await api.get(path, { params, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const el = document.createElement("a");
  el.href = url;
  el.download = filename;
  el.click();
  window.URL.revokeObjectURL(url);
}

export default api;
